import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaCheckCircle,
  FaClock,
  FaPlus,
  FaReceipt,
  FaRedoAlt,
  FaSearch,
  FaStore,
  FaUserEdit,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isAdminUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatCurrency,
  formatDateForInput,
  formatShortDateTime,
  getInitialScopedOutlets,
  getSyncStatusBadgeClass,
  getSyncStatusLabel,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const createVendorBillForm = () => ({
  vendor_name: '',
  bill_no: '',
  bill_date: formatDateForInput(new Date()),
  due_date: '',
  bill_type: 'expense',
  account_purpose: '',
  use_prepaid: false,
  subtotal: '',
  tax_amount: '',
  note: '',
});

const createVendorMasterForm = () => ({
  vendor_name: '',
  contact_name: '',
  phone: '',
  email: '',
  address: '',
  default_payment_term_days: '0',
  is_active: true,
  note: '',
});

const createVendorPaymentForm = () => ({
  vendor_name: '',
  payment_no: '',
  payment_date: formatDateForInput(new Date()),
  payment_method: 'cash',
  note: '',
});

const buildIsoDate = value =>
  value ? new Date(`${value}T00:00:00`).toISOString() : new Date().toISOString();

const normalizeVendorKey = value => String(value || '').trim().toLowerCase();

const buildDueDateWithTerm = (billDateValue, termDays) => {
  const normalizedTerm = Number(termDays || 0);
  if (!billDateValue || normalizedTerm <= 0) {
    return '';
  }

  const nextDate = new Date(`${billDateValue}T00:00:00`);
  if (Number.isNaN(nextDate.getTime())) {
    return '';
  }

  nextDate.setDate(nextDate.getDate() + normalizedTerm);
  return formatDateForInput(nextDate);
};

const buildVendorMasterFormFromRecord = vendor => ({
  vendor_name: String(vendor?.vendor_name || ''),
  contact_name: String(vendor?.contact_name || ''),
  phone: String(vendor?.phone || ''),
  email: String(vendor?.email || ''),
  address: String(vendor?.address || ''),
  default_payment_term_days: String(Number(vendor?.default_payment_term_days || 0)),
  is_active: vendor?.is_active !== false,
  note: String(vendor?.note || ''),
});

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

function PosVendorManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isAdminUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [vendors, setVendors] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [vendorForm, setVendorForm] = useState(createVendorMasterForm);
  const [billForm, setBillForm] = useState(createVendorBillForm);
  const [paymentForm, setPaymentForm] = useState(createVendorPaymentForm);
  const [paymentSelections, setPaymentSelections] = useState({});
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [activePanel, setActivePanel] = useState('monitoring');
  const [loading, setLoading] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);
  const [savingBill, setSavingBill] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    start_date: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 45))),
    end_date: formatDateForInput(new Date()),
  });
  const [errorMessage, setErrorMessage] = useState('');

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const fetchAllData = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setVendors([]);
      setPurchases([]);
      setBills([]);
      setPayments([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const vendorParams = new URLSearchParams({
        outlet_id: outletId,
        include_inactive: '1',
      });
      const purchaseParams = new URLSearchParams({ outlet_id: outletId });
      const financeParams = new URLSearchParams({ outlet_id: outletId });
      if (dateRange.start_date) {
        purchaseParams.set('start_date', dateRange.start_date);
        financeParams.set('start_date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        purchaseParams.set('end_date', dateRange.end_date);
        financeParams.set('end_date', dateRange.end_date);
      }

      const [vendorsResponse, purchaseResponse, billsResponse, paymentsResponse] = await Promise.all([
        fetch(`${POS_API_BASE_URL}api/vendors/?${vendorParams.toString()}`, {
          headers: buildAuthHeaders(token),
        }),
        fetch(`${POS_API_BASE_URL}api/purchase/reports?${purchaseParams.toString()}`, {
          headers: buildAuthHeaders(token),
        }),
        fetch(`${POS_API_BASE_URL}api/vendor-bills/?${financeParams.toString()}`, {
          headers: buildAuthHeaders(token),
        }),
        fetch(`${POS_API_BASE_URL}api/vendor-payments/?${financeParams.toString()}`, {
          headers: buildAuthHeaders(token),
        }),
      ]);

      const [vendorsData, purchaseData, billsData, paymentsData] = await Promise.all([
        parseJsonResponse(vendorsResponse),
        parseJsonResponse(purchaseResponse),
        parseJsonResponse(billsResponse),
        parseJsonResponse(paymentsResponse),
      ]);

      if (!vendorsResponse.ok) {
        throw new Error(buildApiErrorMessage(vendorsResponse, vendorsData, 'Gagal memuat data vendor.'));
      }
      if (!purchaseResponse.ok) {
        throw new Error(buildApiErrorMessage(purchaseResponse, purchaseData, 'Gagal memuat data purchase vendor.'));
      }
      if (!billsResponse.ok) {
        throw new Error(buildApiErrorMessage(billsResponse, billsData, 'Gagal memuat bill vendor.'));
      }
      if (!paymentsResponse.ok) {
        throw new Error(buildApiErrorMessage(paymentsResponse, paymentsData, 'Gagal memuat pembayaran vendor.'));
      }

      const { records: vendorRecords } = normalizePagedRecords(vendorsData);
      const { records: purchaseRecords } = normalizePagedRecords(purchaseData);
      const { records: billRecords } = normalizePagedRecords(billsData);
      const { records: paymentRecords } = normalizePagedRecords(paymentsData);

      setVendors(Array.isArray(vendorRecords) ? vendorRecords : []);
      setPurchases(Array.isArray(purchaseRecords) ? purchaseRecords : []);
      setBills(Array.isArray(billRecords) ? billRecords : []);
      setPayments(Array.isArray(paymentRecords) ? paymentRecords : []);
    } catch (error) {
      console.error('Failed to fetch POS vendors workspace:', error);
      setVendors([]);
      setPurchases([]);
      setBills([]);
      setPayments([]);
      setErrorMessage(error.message || 'Gagal memuat halaman vendor POS.');
    } finally {
      setLoading(false);
    }
  }, [dateRange.end_date, dateRange.start_date]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;

    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error('Failed to initialize POS vendors workspace:', error);
        if (active) {
          setErrorMessage(error.message || 'Gagal menyiapkan halaman vendor POS.');
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchAllData(authToken, selectedOutlet);
  }, [authToken, fetchAllData, selectedOutlet]);

  useEffect(() => {
    setShowVendorModal(false);
    setVendorForm(createVendorMasterForm());
    setEditingVendorId(null);
    setBillForm(createVendorBillForm());
    setPaymentForm(createVendorPaymentForm());
    setPaymentSelections({});
  }, [selectedOutlet]);

  const vendorMasterByName = useMemo(() => {
    const vendorMap = new Map();
    vendors.forEach(vendor => {
      const key = normalizeVendorKey(vendor.vendor_name);
      if (key) {
        vendorMap.set(key, vendor);
      }
    });
    return vendorMap;
  }, [vendors]);

  const vendorDirectory = useMemo(() => {
    const vendorMap = new Map();

    const pickLatestDate = (currentValue, nextValue) => {
      if (!nextValue) {
        return currentValue;
      }
      if (!currentValue) {
        return nextValue;
      }

      const currentDate = new Date(currentValue);
      const nextDate = new Date(nextValue);
      if (Number.isNaN(currentDate.getTime()) || Number.isNaN(nextDate.getTime())) {
        return nextValue;
      }

      return nextDate > currentDate ? nextValue : currentValue;
    };

    const registerVendor = (name, recordType, amount, extra = {}) => {
      const normalizedName = String(name || '').trim();
      if (!normalizedName) {
        return;
      }

      const current = vendorMap.get(normalizedName) || {
        id: extra.id || null,
        name: normalizedName,
        contact_name: extra.contact_name || '',
        phone: extra.phone || '',
        email: extra.email || '',
        address: extra.address || '',
        default_payment_term_days: Number(extra.default_payment_term_days || 0),
        is_active: extra.is_active !== false,
        note: extra.note || '',
        has_master: Boolean(extra.has_master),
        purchases: 0,
        purchase_amount: 0,
        bills: 0,
        outstanding_amount: 0,
        payments: 0,
        payment_amount: 0,
        last_activity_at: '',
        last_type: '',
      };

      if (recordType === 'purchase') {
        current.purchases += 1;
        current.purchase_amount += Number(amount || 0);
      }
      if (recordType === 'bill') {
        current.bills += 1;
        current.outstanding_amount += Number(extra.outstanding_amount || 0);
      }
      if (recordType === 'payment') {
        current.payments += 1;
        current.payment_amount += Number(amount || 0);
      }

      if (extra.has_master) {
        current.id = extra.id || current.id;
        current.contact_name = extra.contact_name || current.contact_name;
        current.phone = extra.phone || current.phone;
        current.email = extra.email || current.email;
        current.address = extra.address || current.address;
        current.default_payment_term_days = Number(extra.default_payment_term_days || current.default_payment_term_days || 0);
        current.is_active = extra.is_active !== false;
        current.note = extra.note || current.note;
        current.has_master = true;
      }

      current.last_activity_at = pickLatestDate(current.last_activity_at, extra.date);
      current.last_type = recordType;
      vendorMap.set(normalizedName, current);
    };

    vendors.forEach(vendor => registerVendor(vendor.vendor_name, 'master', 0, {
      id: vendor.id,
      contact_name: vendor.contact_name,
      phone: vendor.phone,
      email: vendor.email,
      address: vendor.address,
      default_payment_term_days: vendor.default_payment_term_days,
      is_active: vendor.is_active,
      note: vendor.note,
      has_master: true,
    }));
    purchases.forEach(row => registerVendor(row.supplier_name, 'purchase', row.total, { date: row.purchase_date }));
    bills.forEach(row => registerVendor(row.vendor_name, 'bill', row.total_amount, {
      date: row.bill_date,
      outstanding_amount: row.outstanding_amount,
    }));
    payments.forEach(row => registerVendor(row.vendor_name, 'payment', row.amount, { date: row.payment_date }));

    return Array.from(vendorMap.values()).sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1;
      }
      if (a.has_master !== b.has_master) {
        return a.has_master ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [bills, payments, purchases, vendors]);

  const activeVendorOptions = useMemo(
    () => vendors
      .filter(vendor => vendor.is_active !== false)
      .map(vendor => String(vendor.vendor_name || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
    [vendors]
  );

  const vendorOptions = useMemo(() => {
    const optionMap = new Map();
    activeVendorOptions.forEach(option => optionMap.set(option.toLowerCase(), option));
    vendorDirectory.forEach(vendor => optionMap.set(vendor.name.toLowerCase(), vendor.name));
    return Array.from(optionMap.values()).sort((a, b) => a.localeCompare(b));
  }, [activeVendorOptions, vendorDirectory]);

  const filteredVendorDirectory = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      return vendorDirectory;
    }

    return vendorDirectory.filter(row =>
      [
        row.name,
        row.contact_name,
        row.phone,
        row.email,
        row.note,
        row.has_master ? 'master' : 'legacy',
        row.is_active ? 'aktif' : 'nonaktif',
      ].some(value => String(value || '').toLowerCase().includes(keyword))
    );
  }, [search, vendorDirectory]);

  const filteredBills = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      return bills;
    }

    return bills.filter(row =>
      [row.vendor_name, row.bill_no, row.note, row.status]
        .some(value => String(value || '').toLowerCase().includes(keyword))
    );
  }, [bills, search]);

  const filteredPayments = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      return payments;
    }

    return payments.filter(row =>
      [row.vendor_name, row.payment_no, row.note, row.status]
        .some(value => String(value || '').toLowerCase().includes(keyword))
    );
  }, [payments, search]);

  const openBills = useMemo(
    () => bills.filter(row => Number(row.outstanding_amount || 0) > 0.0001),
    [bills]
  );

  const vendorFilteredOpenBills = useMemo(() => {
    const paymentVendor = String(paymentForm.vendor_name || '').trim().toLowerCase();
    if (!paymentVendor) {
      return openBills;
    }
    return openBills.filter(row => String(row.vendor_name || '').trim().toLowerCase() === paymentVendor);
  }, [openBills, paymentForm.vendor_name]);

  const computedBillTotal = Number(billForm.subtotal || 0) + Number(billForm.tax_amount || 0);

  const selectedAllocations = useMemo(
    () =>
      vendorFilteredOpenBills
        .filter(row => paymentSelections[row.id]?.checked)
        .map(row => ({
          vendor_bill_id: row.id,
          vendor_name: row.vendor_name,
          bill_no: row.bill_no,
          amount: Number(paymentSelections[row.id]?.amount || 0),
          outstanding_amount: Number(row.outstanding_amount || 0),
        }))
        .filter(row => row.amount > 0),
    [paymentSelections, vendorFilteredOpenBills]
  );

  const totalAllocationAmount = useMemo(
    () => selectedAllocations.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [selectedAllocations]
  );

  const selectedBillVendorMaster = useMemo(
    () => vendorMasterByName.get(normalizeVendorKey(billForm.vendor_name)) || null,
    [billForm.vendor_name, vendorMasterByName]
  );

  const legacyVendorCount = useMemo(
    () => vendorDirectory.filter(vendor => !vendor.has_master).length,
    [vendorDirectory]
  );

  const inactiveVendorCount = useMemo(
    () => vendors.filter(vendor => vendor.is_active === false).length,
    [vendors]
  );

  const vendorsWithOutstandingCount = useMemo(
    () => vendorDirectory.filter(vendor => Number(vendor.outstanding_amount || 0) > 0.0001).length,
    [vendorDirectory]
  );

  const summary = useMemo(() => {
    const openBillAmount = bills.reduce((sum, row) => sum + Number(row.outstanding_amount || 0), 0);
    const paymentAmount = payments.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const overdueCount = bills.filter(row => {
      if (!row.due_date || Number(row.outstanding_amount || 0) <= 0.0001) {
        return false;
      }
      const dueDate = new Date(row.due_date);
      return !Number.isNaN(dueDate.getTime()) && dueDate < new Date();
    }).length;

    return {
      vendors: vendors.filter(vendor => vendor.is_active !== false).length || vendorDirectory.filter(vendor => vendor.is_active !== false).length,
      openBillAmount,
      overdueCount,
      paymentAmount,
      legacyVendorCount,
      inactiveVendorCount,
      vendorsWithOutstandingCount,
    };
  }, [bills, inactiveVendorCount, legacyVendorCount, payments, vendorDirectory, vendors, vendorsWithOutstandingCount]);

  const handleVendorFormChange = event => {
    const { name, value, type, checked } = event.target;
    setVendorForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleBillFormChange = event => {
    const { name, value, type, checked } = event.target;
    setBillForm(prev => {
      const nextForm = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if ((name === 'vendor_name' || name === 'bill_date') && !String(prev.due_date || '').trim()) {
        const vendor = vendorMasterByName.get(normalizeVendorKey(name === 'vendor_name' ? value : prev.vendor_name));
        if (vendor && Number(vendor.default_payment_term_days || 0) > 0) {
          nextForm.due_date = buildDueDateWithTerm(
            name === 'bill_date' ? value : prev.bill_date,
            vendor.default_payment_term_days
          );
        }
      }

      return nextForm;
    });
  };

  const handlePaymentFormChange = event => {
    const { name, value } = event.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAllocationToggle = useCallback((bill, checked) => {
    setPaymentSelections(prev => ({
      ...prev,
      [bill.id]: {
        checked,
        amount: checked
          ? String(prev[bill.id]?.amount || Number(bill.outstanding_amount || 0))
          : '',
      },
    }));
  }, []);

  const handleAllocationAmountChange = useCallback((billId, value) => {
    setPaymentSelections(prev => ({
      ...prev,
      [billId]: {
        ...(prev[billId] || { checked: true }),
        checked: true,
        amount: value,
      },
    }));
  }, []);

  const resetVendorMasterForm = useCallback(() => {
    setVendorForm(createVendorMasterForm());
    setEditingVendorId(null);
  }, []);

  const closeVendorModal = useCallback(() => {
    setShowVendorModal(false);
    resetVendorMasterForm();
  }, [resetVendorMasterForm]);

  const openCreateVendorModal = useCallback(() => {
    resetVendorMasterForm();
    setShowVendorModal(true);
    setActivePanel('monitoring');
  }, [resetVendorMasterForm]);

  const jumpToVendorBill = useCallback(vendorRecord => {
    const vendorName = String(vendorRecord?.vendor_name || vendorRecord?.name || '').trim();
    const matchedVendor = vendorMasterByName.get(normalizeVendorKey(vendorName));
    const nextBillDate = formatDateForInput(new Date());

    setBillForm({
      ...createVendorBillForm(),
      vendor_name: vendorName,
      bill_date: nextBillDate,
      due_date: buildDueDateWithTerm(nextBillDate, matchedVendor?.default_payment_term_days),
    });
    setActivePanel('bill');
  }, [vendorMasterByName]);

  const jumpToVendorPayment = useCallback(vendorRecord => {
    const vendorName = String(vendorRecord?.vendor_name || vendorRecord?.name || '').trim();
    setPaymentForm({
      ...createVendorPaymentForm(),
      vendor_name: vendorName,
    });
    setPaymentSelections({});
    setActivePanel('payment');
  }, []);

  const handleEditVendor = useCallback(vendor => {
    setVendorForm(buildVendorMasterFormFromRecord(vendor));
    setEditingVendorId(vendor.id);
    setShowVendorModal(true);
    setActivePanel('monitoring');
  }, []);

  const handleSeedVendorMaster = useCallback(vendor => {
    const existingVendor = vendorMasterByName.get(normalizeVendorKey(vendor.name));
    if (existingVendor) {
      handleEditVendor(existingVendor);
      return;
    }

    setVendorForm({
      ...createVendorMasterForm(),
      vendor_name: vendor.name,
      default_payment_term_days: String(Number(vendor.default_payment_term_days || 0)),
    });
    setEditingVendorId(null);
    setShowVendorModal(true);
    setActivePanel('monitoring');
  }, [handleEditVendor, vendorMasterByName]);

  const handleSaveVendor = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSavingVendor(true);
    try {
      const vendorName = String(vendorForm.vendor_name || '').trim();
      if (!vendorName) {
        throw new Error('Nama vendor wajib diisi.');
      }

      const payload = {
        outlet_id: Number(selectedOutlet),
        vendor_name: vendorName,
        contact_name: String(vendorForm.contact_name || '').trim(),
        phone: String(vendorForm.phone || '').trim(),
        email: String(vendorForm.email || '').trim(),
        address: String(vendorForm.address || '').trim(),
        default_payment_term_days: Number(vendorForm.default_payment_term_days || 0),
        is_active: Boolean(vendorForm.is_active),
        note: String(vendorForm.note || '').trim(),
      };

      const response = await fetch(
        `${POS_API_BASE_URL}api/vendors/${editingVendorId ? `${editingVendorId}` : ''}`,
        {
          method: editingVendorId ? 'PUT' : 'POST',
          headers: buildAuthHeaders(authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan data vendor.'));
      }

      closeVendorModal();
      await fetchAllData(authToken, selectedOutlet);
      Swal.fire('Success', editingVendorId ? 'Data vendor berhasil diperbarui.' : 'Data vendor berhasil ditambahkan.', 'success');
    } catch (error) {
      console.error('Failed to save vendor master:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan data vendor.', 'error');
    } finally {
      setSavingVendor(false);
    }
  }, [authToken, closeVendorModal, editingVendorId, fetchAllData, selectedOutlet, vendorForm]);

  const handleCreateVendorBill = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSavingBill(true);
    try {
      const vendorName = String(billForm.vendor_name || '').trim();
      const matchedVendor = vendorMasterByName.get(normalizeVendorKey(vendorName));
      if (!vendorName) {
        throw new Error('Nama vendor wajib diisi.');
      }
      if (!matchedVendor) {
        throw new Error('Vendor belum terdaftar. Tambahkan terlebih dahulu dari panel Monitoring.');
      }
      if (matchedVendor.is_active === false) {
        throw new Error('Vendor nonaktif tidak dapat digunakan untuk bill baru.');
      }
      if (computedBillTotal <= 0) {
        throw new Error('Nilai bill harus lebih besar dari nol.');
      }

      const payload = {
        outlet_id: Number(selectedOutlet),
        vendor_name: vendorName,
        bill_no: String(billForm.bill_no || '').trim(),
        bill_date: buildIsoDate(billForm.bill_date),
        due_date: billForm.due_date ? buildIsoDate(billForm.due_date) : null,
        bill_type: String(billForm.bill_type || 'expense').trim(),
        account_purpose: String(billForm.account_purpose || '').trim(),
        use_prepaid: Boolean(billForm.use_prepaid),
        subtotal: Number(billForm.subtotal || 0),
        tax_amount: Number(billForm.tax_amount || 0),
        total_amount: computedBillTotal,
        note: String(billForm.note || '').trim(),
      };

      const response = await fetch(`${POS_API_BASE_URL}api/vendor-bills/`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan bill vendor.'));
      }

      setBillForm(createVendorBillForm());
      await fetchAllData(authToken, selectedOutlet);
      Swal.fire('Success', 'Bill vendor berhasil dicatat.', 'success');
    } catch (error) {
      console.error('Failed to create vendor bill:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan bill vendor.', 'error');
    } finally {
      setSavingBill(false);
    }
  };

  const handleCreateVendorPayment = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSavingPayment(true);
    try {
      if (selectedAllocations.length === 0) {
        throw new Error('Pilih minimal satu bill untuk dibayar.');
      }

      const payload = {
        outlet_id: Number(selectedOutlet),
        vendor_name: String(paymentForm.vendor_name || '').trim(),
        payment_no: String(paymentForm.payment_no || '').trim(),
        payment_date: buildIsoDate(paymentForm.payment_date),
        payment_method: String(paymentForm.payment_method || 'cash').trim(),
        amount: totalAllocationAmount,
        note: String(paymentForm.note || '').trim(),
        allocations: selectedAllocations.map(allocation => ({
          vendor_bill_id: allocation.vendor_bill_id,
          amount: allocation.amount,
        })),
      };

      const response = await fetch(`${POS_API_BASE_URL}api/vendor-payments/`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan pembayaran vendor.'));
      }

      setPaymentForm(createVendorPaymentForm());
      setPaymentSelections({});
      await fetchAllData(authToken, selectedOutlet);
      Swal.fire('Success', 'Pembayaran vendor berhasil dicatat.', 'success');
    } catch (error) {
      console.error('Failed to create vendor payment:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan pembayaran vendor.', 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleViewBill = useCallback(async billId => {
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/vendor-bills/${billId}`, {
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat detail bill vendor.'));
      }

      await Swal.fire({
        title: `Bill Vendor #${data.id}`,
        html: `
          <div style="text-align:left">
            <p><strong>Vendor:</strong> ${escapeHtml(data.vendor_name || '-')}</p>
            <p><strong>Nomor Bill:</strong> ${escapeHtml(data.bill_no || '-')}</p>
            <p><strong>Tanggal:</strong> ${escapeHtml(formatShortDateTime(data.bill_date))}</p>
            <p><strong>Jatuh Tempo:</strong> ${escapeHtml(formatShortDateTime(data.due_date))}</p>
            <p><strong>Tipe:</strong> ${escapeHtml(data.bill_type || '-')}</p>
            <p><strong>Sumber:</strong> ${escapeHtml(data.purchase_id ? `Purchase #${data.purchase_id}` : 'Bill vendor manual')}</p>
            <p><strong>Total:</strong> ${escapeHtml(formatCurrency(data.total_amount))}</p>
            <p><strong>Saldo Terbuka:</strong> ${escapeHtml(formatCurrency(data.outstanding_amount))}</p>
            <p><strong>Catatan:</strong> ${escapeHtml(data.note || '-')}</p>
          </div>
        `,
        width: 640,
        confirmButtonText: 'Tutup',
      });
    } catch (error) {
      console.error('Failed to load vendor bill detail:', error);
      Swal.fire('Error', error.message || 'Gagal memuat detail bill vendor.', 'error');
    }
  }, [authToken]);

  const handleViewPayment = useCallback(async paymentId => {
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/vendor-payments/${paymentId}`, {
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat detail pembayaran vendor.'));
      }

      const allocationHtml = Array.isArray(data.allocations) && data.allocations.length > 0
        ? data.allocations.map(allocation => `
            <tr>
              <td>${escapeHtml(allocation?.bill?.bill_no || `#${allocation?.vendor_bill_id || '-'}`)}</td>
              <td>${escapeHtml(allocation?.bill?.vendor_name || data.vendor_name || '-')}</td>
              <td>${escapeHtml(formatCurrency(allocation?.allocated_amount || 0))}</td>
            </tr>
          `).join('')
        : '<tr><td colspan="3">Belum ada alokasi.</td></tr>';

      await Swal.fire({
        title: `Pembayaran Vendor #${data.id}`,
        html: `
          <div style="text-align:left">
            <p><strong>Vendor:</strong> ${escapeHtml(data.vendor_name || '-')}</p>
            <p><strong>Nomor Pembayaran:</strong> ${escapeHtml(data.payment_no || '-')}</p>
            <p><strong>Tanggal:</strong> ${escapeHtml(formatShortDateTime(data.payment_date))}</p>
            <p><strong>Metode:</strong> ${escapeHtml(data.payment_method || '-')}</p>
            <p><strong>Jumlah:</strong> ${escapeHtml(formatCurrency(data.amount))}</p>
            <div style="overflow:auto">
              <table style="width:100%;border-collapse:collapse">
                <thead>
                  <tr>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Bill</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Vendor</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Alokasi</th>
                  </tr>
                </thead>
                <tbody>${allocationHtml}</tbody>
              </table>
            </div>
          </div>
        `,
        width: 760,
        confirmButtonText: 'Tutup',
      });
    } catch (error) {
      console.error('Failed to load vendor payment detail:', error);
      Swal.fire('Error', error.message || 'Gagal memuat detail pembayaran vendor.', 'error');
    }
  }, [authToken]);

  const billColumns = useMemo(() => ([
    {
      name: 'Nomor Bill',
      selector: row => row.bill_no,
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.bill_no || `#${row.id}`}</strong>
          <small>{formatShortDateTime(row.bill_date)}</small>
        </span>
      ),
    },
    {
      name: 'Vendor',
      selector: row => row.vendor_name,
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.vendor_name || '-'}</strong>
          <small>
            {row.bill_type === 'inventory' && row.purchase_id
              ? `inventory • otomatis dari purchase #${row.purchase_id}`
              : (row.bill_type || '-')}
          </small>
        </span>
      ),
    },
    {
      name: 'Total / Saldo Terbuka',
      selector: row => Number(row.total_amount || 0),
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.total_amount)}</strong>
          <small>Sisa {formatCurrency(row.outstanding_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      cell: row => (
        <span className={getSyncStatusBadgeClass(row.accounting_sync_status)}>
          {String(row.status || 'open').replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      name: 'Aksi',
      width: '120px',
      cell: row => (
        <Button variant="outline-secondary" size="sm" onClick={() => handleViewBill(row.id)}>
          Detail
        </Button>
      ),
    },
  ]), [handleViewBill]);

  const paymentColumns = useMemo(() => ([
    {
      name: 'Nomor Pembayaran',
      selector: row => row.payment_no,
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.payment_no || `#${row.id}`}</strong>
          <small>{formatShortDateTime(row.payment_date)}</small>
        </span>
      ),
    },
    {
      name: 'Vendor',
      selector: row => row.vendor_name,
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.vendor_name || '-'}</strong>
          <small>{row.payment_method || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Nilai',
      selector: row => Number(row.amount || 0),
      sortable: true,
      right: true,
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
    {
      name: 'Sinkronisasi',
      selector: row => row.accounting_sync_status,
      sortable: true,
      cell: row => (
        <span className={getSyncStatusBadgeClass(row.accounting_sync_status)}>
          {getSyncStatusLabel(row.accounting_sync_status)}
        </span>
      ),
    },
    {
      name: 'Aksi',
      width: '120px',
      cell: row => (
        <Button variant="outline-secondary" size="sm" onClick={() => handleViewPayment(row.id)}>
          Detail
        </Button>
      ),
    },
  ]), [handleViewPayment]);

  const meta = (
    <>
      <span className="page-meta__chip">
        <small>Outlet aktif</small>
        <strong>{selectedOutletName}</strong>
      </span>
      <span className="page-meta__chip">
        <small>Vendor aktif</small>
        <strong>{summary.vendors}</strong>
      </span>
      <span className="page-meta__chip">
        <small>Saldo terbuka</small>
        <strong>{formatCurrency(summary.openBillAmount)}</strong>
      </span>
    </>
  );

  const actions = (
    <>
      <div className="page-action-group">
        <Form.Select
          className="page-action-select"
          value={selectedOutlet}
          onChange={event => setSelectedOutlet(event.target.value)}
          disabled={outlets.length <= 1}
        >
          {outlets.map(outlet => (
            <option key={outlet.id} value={outlet.id}>
              {outlet.name}
            </option>
          ))}
        </Form.Select>
        <div className="toolbar-search-shell">
          <FaSearch />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Cari vendor, bill, atau pembayaran..."
          />
        </div>
      </div>
      <Button
        variant="outline-secondary"
        onClick={() => authToken && selectedOutlet && fetchAllData(authToken, selectedOutlet)}
      >
        <FaRedoAlt className="me-2" />
        Refresh
      </Button>
    </>
  );

  const panelTabs = [
    { key: 'monitoring', label: 'Monitoring', icon: FaStore },
    { key: 'bill', label: 'Bill Vendor', icon: FaReceipt },
    { key: 'payment', label: 'Pembayaran Vendor', icon: FaWallet },
  ];

  return (
    <Fragment>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaStore}
          eyebrow="Catalog & Inventory"
          title="Vendors & Payables"
          description="Halaman vendor untuk memantau pemasok, bill vendor, pembayaran vendor, dan saldo hutang outlet."
          meta={meta}
          actions={actions}
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: 'Vendors' },
          ]}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaUsers />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Vendor aktif</p>
                <strong>{summary.vendors}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaReceipt />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Saldo bill terbuka</p>
                <strong>{formatCurrency(summary.openBillAmount)}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaWallet />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Pembayaran tercatat</p>
                <strong>{formatCurrency(summary.paymentAmount)}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">Bill Jatuh Tempo</span>
              <h2>{summary.overdueCount}</h2>
              <p>Jumlah bill vendor yang masih terbuka dan telah melewati tanggal jatuh tempo.</p>
            </article>
          </div>

          <div className="pos-catalog-kpi-grid">
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaUserEdit />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Belum terdaftar</p>
                <strong>{summary.legacyVendorCount}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaClock />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Vendor dengan saldo terbuka</p>
                <strong>{summary.vendorsWithOutstandingCount}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaCheckCircle />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Vendor nonaktif</p>
                <strong>{summary.inactiveVendorCount}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">Alur Kerja</span>
              <h2>Daftarkan vendor, catat bill, lalu catat pembayaran</h2>
              <p>Data vendor yang terstruktur membantu pembacaan termin, kontak PIC, dan posisi hutang per outlet secara konsisten.</p>
            </article>
          </div>

          <div className="pos-catalog-panel-tabs">
            {panelTabs.map(panel => {
              const PanelIcon = panel.icon;
              const isActive = activePanel === panel.key;
              return (
                <button
                  key={panel.key}
                  type="button"
                  className={`pos-catalog-panel-tab${isActive ? ' is-active' : ''}`}
                  onClick={() => setActivePanel(panel.key)}
                >
                  <PanelIcon />
                  {panel.label}
                </button>
              );
            })}
          </div>

          {activePanel === 'monitoring' ? (
            <Fragment>
              <div className="surface-card pos-catalog-note-card">
                <span className="page-eyebrow">Monitoring</span>
                <h2>Direktori vendor dan posisi hutang</h2>
                <p>Kelola data vendor, kontak, termin pembayaran default, status aktif/nonaktif, dan pantau hutang vendor per outlet.</p>
              </div>

              <div className="pos-catalog-panel-tabs">
                <button type="button" className="pos-catalog-panel-tab" onClick={openCreateVendorModal}>
                  <FaPlus />
                  Tambah Vendor
                </button>
                <button type="button" className="pos-catalog-panel-tab" onClick={() => setActivePanel('bill')}>
                  <FaReceipt />
                  Catat Bill
                </button>
                <button type="button" className="pos-catalog-panel-tab" onClick={() => setActivePanel('payment')}>
                  <FaWallet />
                  Catat Pembayaran
                </button>
                {summary.legacyVendorCount > 0 ? (
                  <button type="button" className="pos-catalog-panel-tab" onClick={() => setSearch('legacy')}>
                    <FaUserEdit />
                    Tinjau Historis
                  </button>
                ) : null}
              </div>

              <div className="pos-catalog-layout">
                <section className="surface-card pos-catalog-form-card">
                  <div className="pos-catalog-form-head">
                    <div>
                      <span className="page-eyebrow">Data Vendor</span>
                      <h2>Kelola vendor melalui dialog terpisah</h2>
                      <p>Input vendor dipisahkan dari panel monitoring agar halaman tetap rapi dan fokus pada daftar vendor serta posisi hutang.</p>
                    </div>
                    <Button variant="primary" onClick={openCreateVendorModal}>
                      <FaPlus className="me-2" />
                      Tambah Vendor
                    </Button>
                  </div>

                  <div className="pos-catalog-subsection">
                    <div className="pos-catalog-section-head">
                      <div>
                        <h3>Form terpisah</h3>
                        <p className="m-0">Admin dapat menambah atau memperbarui vendor tanpa mengganggu keterbacaan halaman monitoring.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <span className="pos-catalog-badge pos-catalog-badge--success">
                        Kontak PIC
                      </span>
                      <span className="pos-catalog-badge pos-catalog-badge--info">
                        Termin default
                      </span>
                      <span className="pos-catalog-badge pos-catalog-badge--warning">
                        Status aktif/nonaktif
                      </span>
                    </div>
                  </div>

                  <div className="pos-catalog-subsection">
                    <div className="pos-catalog-section-head">
                      <div>
                        <h3>Vendor yang dikelola</h3>
                      <p className="m-0">Daftar vendor terdaftar pada outlet aktif.</p>
                      </div>
                    </div>
                    <div className="pos-catalog-vendor-list">
                      {vendors.length === 0 ? (
                        <p className="m-0 text-muted">Belum ada vendor terdaftar pada outlet ini.</p>
                      ) : vendors.map(vendor => (
                        <article key={vendor.id} className="pos-catalog-vendor-card">
                          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                            <div className="d-grid gap-1">
                              <strong>{vendor.vendor_name}</strong>
                              <span>{vendor.contact_name || 'Belum ada PIC'} • {vendor.phone || 'Tanpa telepon'}</span>
                              <small>{vendor.email || 'Tanpa email'}</small>
                            </div>
                            <span className={`pos-catalog-badge ${vendor.is_active ? 'pos-catalog-badge--success' : 'pos-catalog-badge--danger'}`}>
                              {vendor.is_active ? 'Aktif' : 'Nonaktif'}
                            </span>
                          </div>
                          <small>
                            Termin {Number(vendor.default_payment_term_days || 0)} hari
                            {vendor.note ? ` • ${vendor.note}` : ''}
                          </small>
                          <div className="d-flex gap-2 flex-wrap">
                            <Button variant="outline-secondary" size="sm" onClick={() => handleEditVendor(vendor)}>
                              Edit
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => jumpToVendorBill(vendor)}>
                              Catat Bill
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => jumpToVendorPayment(vendor)}>
                              Catat Pembayaran
                            </Button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="surface-card pos-catalog-form-card">
                  <div className="pos-catalog-form-head">
                    <div>
                      <span className="page-eyebrow">Direktori Vendor</span>
                      <h2>Data vendor dan riwayat transaksi</h2>
                      <p>Gabungan data vendor dengan aktivitas purchase, bill vendor, dan pembayaran vendor yang sudah tercatat.</p>
                    </div>
                  </div>

                  <div className="pos-catalog-vendor-list">
                    {filteredVendorDirectory.length === 0 ? (
                      <p className="m-0 text-muted">Belum ada vendor tercatat di outlet ini.</p>
                    ) : filteredVendorDirectory.map(vendor => (
                      <article key={vendor.name} className="pos-catalog-vendor-card">
                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                          <div className="d-grid gap-1">
                            <strong>{vendor.name}</strong>
                            <span>
                              {vendor.has_master ? (vendor.contact_name || 'Terdaftar tanpa PIC') : 'Belum terdaftar'}
                              {vendor.phone ? ` • ${vendor.phone}` : ''}
                            </span>
                            <small>
                              Purchase {vendor.purchases} • Bill {vendor.bills} • Pembayaran {vendor.payments}
                            </small>
                          </div>
                          <span className={`pos-catalog-badge ${
                            vendor.has_master
                              ? vendor.is_active
                                ? 'pos-catalog-badge--success'
                                : 'pos-catalog-badge--danger'
                              : 'pos-catalog-badge--muted'
                          }`}>
                            {vendor.has_master ? (vendor.is_active ? 'Terdaftar aktif' : 'Terdaftar nonaktif') : 'Belum terdaftar'}
                          </span>
                        </div>
                        <small>
                          Saldo terbuka {formatCurrency(vendor.outstanding_amount)} • Terbayar {formatCurrency(vendor.payment_amount)}
                          {vendor.default_payment_term_days > 0 ? ` • Termin ${vendor.default_payment_term_days} hari` : ''}
                          {vendor.last_activity_at ? ` • Aktivitas ${formatShortDateTime(vendor.last_activity_at)}` : ''}
                        </small>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleSeedVendorMaster(vendor)}
                          >
                            {vendor.has_master ? 'Edit Data' : 'Daftarkan Vendor'}
                          </Button>
                          {vendor.has_master && vendor.is_active ? (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => jumpToVendorBill(vendor)}
                            >
                              Catat Bill
                            </Button>
                          ) : null}
                          {Number(vendor.outstanding_amount || 0) > 0.0001 ? (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => jumpToVendorPayment(vendor)}
                            >
                              Catat Pembayaran
                            </Button>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </div>

              <section className="surface-card table-card pos-catalog-table-card">
                <div className="pos-catalog-toolbar">
                  <div>
                    <span className="page-eyebrow">Bill Vendor</span>
                    <h2 className="mb-1">Riwayat hutang vendor</h2>
                    <p className="text-muted mb-0">Pantau dokumen hutang vendor dan posisi saldo terbuka pada outlet aktif, termasuk bill inventory otomatis dari purchase tempo.</p>
                  </div>
                  <div className="pos-catalog-toolbar__meta">
                    <span className="pos-catalog-filter-chip">{filteredBills.length} bill</span>
                    <span className="pos-catalog-filter-chip">Saldo terbuka {formatCurrency(summary.openBillAmount)}</span>
                    <Form.Control
                      type="date"
                      value={dateRange.start_date}
                      onChange={event =>
                        setDateRange(prev => ({ ...prev, start_date: event.target.value }))
                      }
                    />
                    <Form.Control
                      type="date"
                      value={dateRange.end_date}
                      onChange={event =>
                        setDateRange(prev => ({ ...prev, end_date: event.target.value }))
                      }
                    />
                  </div>
                </div>

                <AdminDataTable
                  columns={billColumns}
                  data={filteredBills}
                  progressPending={loading}
                  pagination
                  emptyTitle="Belum ada bill vendor"
                  emptyDescription="Seluruh bill vendor POS akan muncul di sini setelah mulai dicatat."
                />
              </section>

              <section className="surface-card table-card pos-catalog-table-card">
                <div className="pos-catalog-toolbar">
                  <div>
                    <span className="page-eyebrow">Pembayaran Vendor</span>
                    <h2 className="mb-1">Riwayat pembayaran vendor</h2>
                    <p className="text-muted mb-0">Lihat seluruh pembayaran vendor yang sudah dicatat dari backoffice admin, termasuk pelunasan purchase tempo.</p>
                  </div>
                  <div className="pos-catalog-toolbar__meta">
                    <span className="pos-catalog-filter-chip">Pembayaran {filteredPayments.length}</span>
                    <span className="pos-catalog-filter-chip">Total {formatCurrency(summary.paymentAmount)}</span>
                  </div>
                </div>

                <AdminDataTable
                  columns={paymentColumns}
                  data={filteredPayments}
                  progressPending={loading}
                  pagination
                  emptyTitle="Belum ada pembayaran vendor"
                  emptyDescription="Pembayaran vendor yang dicatat dari backoffice akan muncul di sini."
                />
              </section>
            </Fragment>
          ) : null}

          {activePanel === 'bill' ? (
            <Fragment>
              <div className="surface-card pos-catalog-note-card">
                <span className="page-eyebrow">Bill Vendor</span>
                <h2>Pencatatan tagihan vendor</h2>
                <p>Gunakan untuk kewajiban non-stok seperti expense, asset, atau rent. Tagihan persediaan tidak dicatat di sini.</p>
              </div>

              <div className="surface-card pos-catalog-note-card">
                <span className="page-eyebrow">Aturan dokumen</span>
                <h2>Hindari duplikasi dokumen</h2>
                <p>Jika barang fisik sudah masuk outlet dan stok harus bertambah, gunakan menu Purchase. Bill vendor digunakan khusus kewajiban non-stok agar tidak terjadi duplikasi dengan stok masuk.</p>
              </div>

              <div className="pos-catalog-layout">
                <section className="surface-card pos-catalog-form-card">
                  <div className="pos-catalog-form-head">
                    <div>
                      <span className="page-eyebrow">Form Baru</span>
                      <h2>Catat bill vendor</h2>
                      <p>Dokumen ini membentuk kewajiban vendor dan status saldo terbuka untuk expense, asset, atau rent.</p>
                    </div>
                  </div>

                  <Form onSubmit={handleCreateVendorBill} className="d-grid gap-3">
                    <Form.Group>
                      <Form.Label>Vendor</Form.Label>
                      <Form.Control
                        list="pos-active-vendor-options"
                        name="vendor_name"
                        value={billForm.vendor_name}
                        onChange={handleBillFormChange}
                        placeholder="Nama vendor"
                      />
                      <div className="pos-catalog-field-note">
                        Bill baru hanya dapat dibuat untuk vendor yang sudah terdaftar dan masih aktif.
                      </div>
                      {selectedBillVendorMaster ? (
                        <div className="pos-catalog-field-note">
                          Termin default {Number(selectedBillVendorMaster.default_payment_term_days || 0)} hari
                          {selectedBillVendorMaster.contact_name ? ` • PIC ${selectedBillVendorMaster.contact_name}` : ''}
                        </div>
                      ) : null}
                    </Form.Group>

                    <div className="pos-catalog-mini-grid">
                      <Form.Group>
                        <Form.Label>Nomor bill</Form.Label>
                        <Form.Control
                          name="bill_no"
                          value={billForm.bill_no}
                          onChange={handleBillFormChange}
                          placeholder="BILL-..."
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Jenis bill</Form.Label>
                        <Form.Select
                          name="bill_type"
                          value={billForm.bill_type}
                          onChange={handleBillFormChange}
                        >
                          <option value="expense">Expense</option>
                          <option value="asset">Asset</option>
                          <option value="rent">Rent</option>
                        </Form.Select>
                        <div className="pos-catalog-field-note">
                          Inventory tidak dicatat melalui bill vendor. Jika stok barang masuk outlet, gunakan Purchase.
                        </div>
                      </Form.Group>
                    </div>

                    <div className="pos-catalog-mini-grid">
                      <Form.Group>
                        <Form.Label>Tanggal bill</Form.Label>
                        <Form.Control
                          type="date"
                          name="bill_date"
                          value={billForm.bill_date}
                          onChange={handleBillFormChange}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Jatuh tempo</Form.Label>
                        <Form.Control
                          type="date"
                          name="due_date"
                          value={billForm.due_date}
                          onChange={handleBillFormChange}
                        />
                      </Form.Group>
                    </div>

                    <div className="pos-catalog-mini-grid">
                      <Form.Group>
                        <Form.Label>Tujuan akun</Form.Label>
                        <Form.Control
                          name="account_purpose"
                          value={billForm.account_purpose}
                          onChange={handleBillFormChange}
                          placeholder="Contoh: packaging, sewa, listrik"
                        />
                      </Form.Group>
                      <Form.Group className="d-flex align-items-end">
                        <Form.Check
                          type="switch"
                          id="vendor-bill-prepaid"
                          name="use_prepaid"
                          label="Catat sebagai prepaid"
                          checked={billForm.use_prepaid}
                          onChange={handleBillFormChange}
                        />
                      </Form.Group>
                    </div>

                    <div className="pos-catalog-mini-grid">
                      <Form.Group>
                        <Form.Label>Subtotal</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          name="subtotal"
                          value={billForm.subtotal}
                          onChange={handleBillFormChange}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Pajak</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          name="tax_amount"
                          value={billForm.tax_amount}
                          onChange={handleBillFormChange}
                        />
                      </Form.Group>
                    </div>

                    <Form.Group>
                      <Form.Label>Catatan</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="note"
                        value={billForm.note}
                        onChange={handleBillFormChange}
                        placeholder="Catatan bill"
                      />
                    </Form.Group>

                    <div className="pos-catalog-document-total">
                      <span>Total bill</span>
                      <strong>{formatCurrency(computedBillTotal)}</strong>
                    </div>

                    <div className="pos-catalog-form-actions">
                      <Button variant="outline-secondary" type="button" onClick={() => setBillForm(createVendorBillForm())}>
                        Reset
                      </Button>
                      <Button variant="primary" type="submit" disabled={savingBill}>
                        {savingBill ? 'Menyimpan...' : 'Simpan Bill Vendor'}
                      </Button>
                    </div>
                  </Form>
                </section>

                <section className="surface-card table-card pos-catalog-table-card">
                  <div className="pos-catalog-toolbar">
                    <div>
                      <span className="page-eyebrow">Data Bill</span>
                      <h2 className="mb-1">Dokumen bill vendor</h2>
                      <p className="text-muted mb-0">Gunakan tabel ini untuk meninjau dokumen manual dan bill inventory otomatis dari purchase tempo.</p>
                    </div>
                    <div className="pos-catalog-toolbar__meta">
                      <span className="pos-catalog-filter-chip">{filteredBills.length} bill</span>
                      <span className="pos-catalog-filter-chip">Saldo terbuka {formatCurrency(summary.openBillAmount)}</span>
                      <Form.Control
                        type="date"
                        value={dateRange.start_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, start_date: event.target.value }))
                        }
                      />
                      <Form.Control
                        type="date"
                        value={dateRange.end_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, end_date: event.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <AdminDataTable
                    columns={billColumns}
                    data={filteredBills}
                    progressPending={loading}
                    pagination
                    emptyTitle="Belum ada bill vendor"
                    emptyDescription="Seluruh bill vendor POS akan muncul di sini setelah mulai dicatat."
                  />
                </section>
              </div>
            </Fragment>
          ) : null}

          {activePanel === 'payment' ? (
            <Fragment>
              <div className="surface-card pos-catalog-note-card">
                <span className="page-eyebrow">Pembayaran Vendor</span>
                <h2>Catat pembayaran vendor</h2>
                <p>Pilih bill dengan saldo terbuka, tentukan alokasi pembayaran, lalu catat pembayaran dari backoffice.</p>
              </div>

              <div className="pos-catalog-layout">
                <section className="surface-card pos-catalog-form-card">
                  <div className="pos-catalog-form-head">
                    <div>
                      <span className="page-eyebrow">Form Baru</span>
                      <h2>Catat pembayaran vendor</h2>
                      <p>Pilihan vendor akan menyaring bill dengan saldo terbuka yang dapat dialokasikan.</p>
                    </div>
                  </div>

                  <Form onSubmit={handleCreateVendorPayment} className="d-grid gap-3">
                    <div className="pos-catalog-mini-grid">
                      <Form.Group>
                        <Form.Label>Vendor</Form.Label>
                        <Form.Control
                          list="pos-vendor-options"
                          name="vendor_name"
                          value={paymentForm.vendor_name}
                          onChange={handlePaymentFormChange}
                          placeholder="Filter vendor tertentu"
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Nomor pembayaran</Form.Label>
                        <Form.Control
                          name="payment_no"
                          value={paymentForm.payment_no}
                          onChange={handlePaymentFormChange}
                          placeholder="PAY-..."
                        />
                      </Form.Group>
                    </div>

                    <div className="pos-catalog-mini-grid">
                      <Form.Group>
                        <Form.Label>Tanggal pembayaran</Form.Label>
                        <Form.Control
                          type="date"
                          name="payment_date"
                          value={paymentForm.payment_date}
                          onChange={handlePaymentFormChange}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Metode pembayaran</Form.Label>
                        <Form.Select
                          name="payment_method"
                          value={paymentForm.payment_method}
                          onChange={handlePaymentFormChange}
                        >
                          <option value="cash">Cash</option>
                          <option value="transfer">Transfer</option>
                          <option value="qris">QRIS</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    <Form.Group>
                      <Form.Label>Catatan</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="note"
                        value={paymentForm.note}
                        onChange={handlePaymentFormChange}
                        placeholder="Catatan pembayaran"
                      />
                    </Form.Group>

                    <div className="pos-catalog-subsection">
                      <div className="pos-catalog-section-head">
                        <div>
                          <h3>Pilih bill yang akan dibayar</h3>
                          <p className="m-0">Daftar ini hanya menampilkan bill dengan saldo terbuka.</p>
                        </div>
                      </div>
                      {vendorFilteredOpenBills.length === 0 ? (
                        <p className="m-0 text-muted">Belum ada bill terbuka untuk vendor yang dipilih.</p>
                      ) : (
                        <div className="pos-catalog-document-lines">
                          {vendorFilteredOpenBills.map(bill => {
                            const selection = paymentSelections[bill.id] || {};
                            return (
                              <div key={`bill-allocation-${bill.id}`} className="pos-catalog-document-line">
                                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                                  <Form.Check
                                    type="checkbox"
                                    id={`vendor-bill-${bill.id}`}
                                    label={`${bill.vendor_name || '-'} • ${bill.bill_no || `#${bill.id}`}${bill.bill_type === 'inventory' && bill.purchase_id ? ` • Purchase #${bill.purchase_id}` : ''}`}
                                    checked={Boolean(selection.checked)}
                                    onChange={event => handleAllocationToggle(bill, event.target.checked)}
                                  />
                                  <span className="pos-catalog-badge pos-catalog-badge--warning">
                                    Sisa {formatCurrency(bill.outstanding_amount)}
                                  </span>
                                </div>
                                <div className="pos-catalog-mini-grid">
                                  <Form.Group>
                                    <Form.Label>Nilai alokasi</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      max={Number(bill.outstanding_amount || 0)}
                                      value={selection.amount || ''}
                                      disabled={!selection.checked}
                                      onChange={event => handleAllocationAmountChange(bill.id, event.target.value)}
                                    />
                                  </Form.Group>
                                  <Form.Group>
                                    <Form.Label>Jatuh tempo</Form.Label>
                                    <Form.Control
                                      value={formatShortDateTime(bill.due_date)}
                                      disabled
                                    />
                                  </Form.Group>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="pos-catalog-document-total">
                      <span>Total pembayaran</span>
                      <strong>{formatCurrency(totalAllocationAmount)}</strong>
                    </div>

                    <div className="pos-catalog-form-actions">
                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={() => {
                          setPaymentForm(createVendorPaymentForm());
                          setPaymentSelections({});
                        }}
                      >
                        Reset
                      </Button>
                      <Button variant="primary" type="submit" disabled={savingPayment}>
                        <FaPlus className="me-2" />
                        {savingPayment ? 'Menyimpan...' : 'Simpan Pembayaran'}
                      </Button>
                    </div>
                  </Form>
                </section>

                <section className="surface-card table-card pos-catalog-table-card">
                  <div className="pos-catalog-toolbar">
                    <div>
                      <span className="page-eyebrow">Data Pembayaran</span>
                      <h2 className="mb-1">Dokumen pembayaran vendor</h2>
                      <p className="text-muted mb-0">Lihat pembayaran yang sudah dicatat beserta status sinkronisasi accounting.</p>
                    </div>
                    <div className="pos-catalog-toolbar__meta">
                      <span className="pos-catalog-filter-chip">Pembayaran {filteredPayments.length}</span>
                      <span className="pos-catalog-filter-chip">Total {formatCurrency(summary.paymentAmount)}</span>
                      <Form.Control
                        type="date"
                        value={dateRange.start_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, start_date: event.target.value }))
                        }
                      />
                      <Form.Control
                        type="date"
                        value={dateRange.end_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, end_date: event.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <AdminDataTable
                    columns={paymentColumns}
                    data={filteredPayments}
                    progressPending={loading}
                    pagination
                    emptyTitle="Belum ada pembayaran vendor"
                    emptyDescription="Pembayaran vendor yang dicatat dari backoffice akan muncul di sini."
                  />
                </section>
              </div>
            </Fragment>
          ) : null}
        </div>
        <Modal show={showVendorModal} onHide={closeVendorModal} size="lg" centered>
          <Form onSubmit={handleSaveVendor}>
            <Modal.Header closeButton>
              <Modal.Title>{editingVendorId ? 'Ubah vendor' : 'Tambah vendor'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-grid gap-3">
                <div className="pos-catalog-subsection">
                  <div className="pos-catalog-section-head">
                    <div>
                      <h3>{selectedOutletName}</h3>
                      <p className="m-0">Lengkapi profil vendor agar pencatatan bill dan pembayaran berikutnya menggunakan referensi vendor yang konsisten.</p>
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className="pos-catalog-badge pos-catalog-badge--info">
                      Termin pembayaran
                    </span>
                    <span className="pos-catalog-badge pos-catalog-badge--success">
                      Kontak vendor
                    </span>
                    <span className="pos-catalog-badge pos-catalog-badge--warning">
                      Status aktif
                    </span>
                  </div>
                </div>

                <div className="pos-catalog-mini-grid">
                  <Form.Group>
                    <Form.Label>Nama vendor</Form.Label>
                    <Form.Control
                      name="vendor_name"
                      value={vendorForm.vendor_name}
                      onChange={handleVendorFormChange}
                      placeholder="Contoh: PT Sumber Rasa"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Kontak PIC</Form.Label>
                    <Form.Control
                      name="contact_name"
                      value={vendorForm.contact_name}
                      onChange={handleVendorFormChange}
                      placeholder="Nama PIC vendor"
                    />
                  </Form.Group>
                </div>

                <div className="pos-catalog-mini-grid">
                  <Form.Group>
                    <Form.Label>Telepon</Form.Label>
                    <Form.Control
                      name="phone"
                      value={vendorForm.phone}
                      onChange={handleVendorFormChange}
                      placeholder="08..."
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={vendorForm.email}
                      onChange={handleVendorFormChange}
                      placeholder="vendor@email.com"
                    />
                  </Form.Group>
                </div>

                <div className="pos-catalog-mini-grid">
                  <Form.Group>
                    <Form.Label>Termin default</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      name="default_payment_term_days"
                      value={vendorForm.default_payment_term_days}
                      onChange={handleVendorFormChange}
                    />
                    <div className="pos-catalog-field-note">
                      Nilai ini digunakan untuk mengisi otomatis jatuh tempo bill baru.
                    </div>
                  </Form.Group>
                  <Form.Group className="d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id="vendor-master-active"
                      name="is_active"
                      label="Vendor aktif"
                      checked={Boolean(vendorForm.is_active)}
                      onChange={handleVendorFormChange}
                    />
                  </Form.Group>
                </div>

                <Form.Group>
                  <Form.Label>Alamat</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    value={vendorForm.address}
                    onChange={handleVendorFormChange}
                    placeholder="Alamat vendor"
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Catatan</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="note"
                    value={vendorForm.note}
                    onChange={handleVendorFormChange}
                    placeholder="Catatan vendor"
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" type="button" onClick={closeVendorModal}>
                Batal
              </Button>
              <Button variant="primary" type="submit" disabled={savingVendor}>
                {savingVendor ? 'Menyimpan...' : editingVendorId ? 'Perbarui Vendor' : 'Simpan Vendor'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        <datalist id="pos-active-vendor-options">
          {activeVendorOptions.map(option => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <datalist id="pos-vendor-options">
          {vendorOptions.map(option => (
            <option key={option} value={option} />
          ))}
        </datalist>
      </Container>
    </Fragment>
  );
}

export default PosVendorManager;
